import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Position } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { ArcLayer, PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import { OD_MAP_TYPES } from '../../../constants/odMap';
import { COLORS } from '../../../constants/color';
import { toRgb } from '../../../utils/color';
import { formatCount } from '../../../utils/format';
import type { Node, Edge, Contour } from '../../../types/domain';
import { useAggregationConfig } from '../useAggregationConfig';
import { useOdMapData } from '../../data/useOdMapData';
import { useOdMapConfig } from '../useOdMapConfig';
import { useMapColor } from '../useMapColor';

type UseOdMapLayers = {
  odMapContourLayer: Layer;
  odMapNodeLayer: Layer;
  odMapEdgeLayer: Layer;
};

type NodeData = Omit<Node, 'name'> & { tooltip: string };

type EdgeData = Omit<Edge, 'start_name' | 'end_name'> & { tooltip: string };

const useOdMapLayers = (): UseOdMapLayers => {
  const { t } = useTranslation();
  const { aggregationConfig } = useAggregationConfig();
  const { mapColor } = useMapColor();
  const { odMapType } = useOdMapConfig();
  const { data } = useOdMapData();

  const maxNodeTripCount = useMemo((): number => {
    return data.nodes.reduce((max: number, node: Node): number => {
      return node.trip_count > max ? node.trip_count : max;
    }, 1);
  }, [data.nodes]);

  const nodeData = useMemo((): NodeData[] => {
    return data.nodes.map((node: Node): NodeData => {
      return {
        lat: node.lat,
        lon: node.lon,
        trip_count: node.trip_count,
        tooltip: `${node.name} ${t('tripCounts')}: ${formatCount(node.trip_count)}`,
      };
    });
  }, [data.nodes, t]);

  const maxEdgeTripCount = useMemo((): number => {
    return data.edges.reduce((max: number, edge: Edge): number => {
      return edge.trip_count > max ? edge.trip_count : max;
    }, 1);
  }, [data.edges]);

  const edgeData = useMemo((): EdgeData[] => {
    return data.edges.map((edge: Edge): EdgeData => {
      return {
        start_lat: edge.start_lat,
        start_lon: edge.start_lon,
        end_lat: edge.end_lat,
        end_lon: edge.end_lon,
        trip_count: edge.trip_count,
        tooltip: `${edge.start_name} - ${edge.end_name} ${t('tripCounts')}: ${formatCount(edge.trip_count)}`,
      };
    });
  }, [data.edges, t]);

  const odMapContourLayer = new PolygonLayer<Contour>({
    id: 'odmap-contour-layer',
    data: data.contours,
    getLineWidth: 10,
    lineWidthUnits: 'meters',
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 2,
    getPolygon: (contour: Contour): Position[] => {
      return contour.geometry.coordinates[0];
    },
    getFillColor: toRgb(COLORS.transparent),
    getLineColor: mapColor === 'light' ? toRgb('#000000') : toRgb('#C8C8C8'),
    pickable: false,
    visible: aggregationConfig?.type === 'od_map',
    updateTriggers: {
      getLineColor: [mapColor],
    },
  });

  const odMapNodeLayer = new ScatterplotLayer({
    id: 'odmap-node-layer',
    data: nodeData,
    getPosition: (node: NodeData): [number, number] => {
      return [node.lon, node.lat];
    },
    getRadius: (node: NodeData): number => {
      return node.trip_count / maxNodeTripCount;
    },
    radiusUnits: 'meters',
    radiusScale:
      odMapType === OD_MAP_TYPES.MESH_125M
        ? 60
        : odMapType === OD_MAP_TYPES.MESH_1KM
          ? 300
          : odMapType === OD_MAP_TYPES.TOWN
            ? 90
            : 1000,
    radiusMinPixels: odMapType === OD_MAP_TYPES.CITY ? 10 : 3,
    radiusMaxPixels: 150,
    getFillColor: toRgb('#A260BF'),
    pickable: true,
    visible: aggregationConfig?.type === 'od_map',
    updateTriggers: {
      getRadius: [maxNodeTripCount, odMapType],
    },
  });

  const odMapEdgeLayer = new ArcLayer({
    id: 'odmap-edge-layer',
    data: edgeData,
    getWidth: (edge: EdgeData): number => {
      return edge.trip_count / maxEdgeTripCount;
    },
    widthScale: 15,
    widthUnits: 'pixels',
    widthMinPixels: 1,
    getSourcePosition: (edge: EdgeData): [number, number] => {
      return [edge.start_lon, edge.start_lat];
    },
    getTargetPosition: (edge: EdgeData): [number, number] => {
      return [edge.end_lon, edge.end_lat];
    },
    getSourceColor: toRgb('#A260BF'),
    getTargetColor: toRgb('#A260BF'),
    pickable: true,
    visible: aggregationConfig?.type === 'od_map',
    updateTriggers: {
      getWidth: [maxEdgeTripCount],
    },
  });

  return { odMapContourLayer, odMapNodeLayer, odMapEdgeLayer };
};

export { useOdMapLayers };
